// Logique métier pure du module Tenues (partagée front / back / scripts, testable sans Nuxt).

/** Policy Directus « Logistique Tenues » (créée avec cet identifiant par `provision-tenues-directus.mjs`). */
export const GARMENTS_POLICY_ID = '25061715-7eb5-4633-a35b-0090de6dfb3c'

export const GARMENT_STATUSES = {
  IN_STOCK: 'En stock',
  ASSIGNED: 'Affectée',
  RETURN_REQUESTED: 'Retour demandé',
  RETURNED: 'Retournée',
  DISCARDED: 'Mise au rebut',
  LOST: 'Perdue',
  WRITTEN_OFF: 'Sortie d\'inventaire',
} as const
export type GarmentStatus = keyof typeof GARMENT_STATUSES

export const GARMENT_SOURCES = {
  order_delivery: 'Livraison de commande',
  import: 'Import',
  manual: 'Saisie manuelle',
} as const
export type GarmentSource = keyof typeof GARMENT_SOURCES

export const SIZE_SYSTEMS = ['alpha', 'numeric', 'none'] as const
export type SizeSystem = typeof SIZE_SYSTEMS[number]

export const ALPHA_SIZES = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', '4XL', '5XL'] as const
const NUMERIC_MIN = 28
const NUMERIC_MAX = 64

/** Une pièce de type sans taille ne porte aucune taille ; sinon la taille est obligatoire et appartient au système du type. */
export const isValidSize = (system: SizeSystem | string | null | undefined, size: string | null | undefined): boolean => {
  const value = typeof size === 'string' ? size.trim() : ''
  if (system === 'none') return value === ''
  if (system === 'alpha') return (ALPHA_SIZES as readonly string[]).includes(value)
  if (system === 'numeric') {
    if (!/^\d{2}$/.test(value)) return false
    const n = Number(value)
    return n >= NUMERIC_MIN && n <= NUMERIC_MAX && n % 2 === 0
  }
  return false
}

export const sizeOptions = (system: SizeSystem | string | null | undefined): string[] => {
  if (system === 'alpha') return [...ALPHA_SIZES]
  if (system === 'numeric') return Array.from({ length: (NUMERIC_MAX - NUMERIC_MIN) / 2 + 1 }, (_, i) => String(NUMERIC_MIN + i * 2))
  return []
}

/** Normalise une taille saisie (casse, espaces) ; renvoie null si vide. */
export const normalizeSize = (raw: unknown): string | null => {
  const value = String(raw ?? '').trim().toUpperCase().replace(/\s+/g, '')
  return value === '' ? null : value
}

// ── Machine à états des pièces ───────────────────────────────────────────────

export const GARMENT_ACTIONS = ['assign', 'reassign', 'unassign', 'request_return', 'cancel_return', 'accept_return', 'refuse_return', 'mark_returned', 'restock', 'discard', 'mark_lost', 'write_off', 'transfer'] as const
export type GarmentAction = typeof GARMENT_ACTIONS[number]

const TRANSITIONS: Record<GarmentAction, { from: GarmentStatus[], to: GarmentStatus | null }> = {
  assign: { from: ['IN_STOCK'], to: 'ASSIGNED' },
  // reassign : changement de volontaire d'une pièce déjà affectée (erreur de saisie, prêt, transfert)
  reassign: { from: ['ASSIGNED'], to: 'ASSIGNED' },
  unassign: { from: ['ASSIGNED'], to: 'IN_STOCK' },
  request_return: { from: ['ASSIGNED'], to: 'RETURN_REQUESTED' },
  cancel_return: { from: ['RETURN_REQUESTED'], to: 'ASSIGNED' },
  refuse_return: { from: ['RETURN_REQUESTED'], to: 'ASSIGNED' },
  // accept_return : IN_STOCK (réutilisable) ou DISCARDED (rebut), choisi par la logistique
  accept_return: { from: ['RETURN_REQUESTED'], to: null },
  // mark_returned : pièce rendue via un retour de commande clôturé (à remettre en stock ou au rebut ensuite)
  mark_returned: { from: ['ASSIGNED', 'RETURN_REQUESTED'], to: 'RETURNED' },
  restock: { from: ['RETURNED'], to: 'IN_STOCK' },
  discard: { from: ['RETURNED', 'IN_STOCK'], to: 'DISCARDED' },
  // mark_lost depuis IN_STOCK : pièce manquante constatée lors d'un inventaire
  mark_lost: { from: ['IN_STOCK', 'ASSIGNED', 'RETURN_REQUESTED'], to: 'LOST' },
  write_off: { from: ['IN_STOCK', 'ASSIGNED', 'RETURNED', 'DISCARDED', 'LOST'], to: 'WRITTEN_OFF' },
  // transfer : changement du centre de stockage d'une pièce en stock
  transfer: { from: ['IN_STOCK'], to: 'IN_STOCK' },
}

export const canTransition = (action: GarmentAction, status: string): boolean =>
  TRANSITIONS[action]?.from.includes(status as GarmentStatus) ?? false

export const transitionTarget = (action: GarmentAction): GarmentStatus | null => TRANSITIONS[action].to

/** Statuts dans lesquels la pièce est détenue par un volontaire. */
export const HELD_STATUSES: GarmentStatus[] = ['ASSIGNED', 'RETURN_REQUESTED']

// ── Retours de tenue ─────────────────────────────────────────────────────────

export const GARMENT_RETURN_REASONS = {
  TROP_PETITE: 'Taille trop petite',
  TROP_GRANDE: 'Taille trop grande',
  DETERIOREE: 'Tenue détériorée',
  USURE: 'Usure normale',
  ARRET_VOLONTARIAT: 'Arrêt du volontariat',
  CHANGEMENT_SERVICE: 'Changement de service ou de centre',
  AUTRE: 'Autre',
} as const
export type GarmentReturnReason = keyof typeof GARMENT_RETURN_REASONS

export const GARMENT_RETURN_STATUSES = {
  PENDING: 'En attente de vérification',
  ACCEPTED: 'Acceptée',
  REFUSED: 'Refusée',
  CANCELLED: 'Annulée',
} as const
export type GarmentReturnStatus = keyof typeof GARMENT_RETURN_STATUSES

export const GARMENT_RETURN_OUTCOMES = {
  REFUND_POINTS: 'Remboursement de points',
  EXCHANGE: 'Échange',
  NONE: 'Aucune compensation',
} as const
export type GarmentReturnOutcome = keyof typeof GARMENT_RETURN_OUTCOMES

/** Motifs de refus proposés à la logistique (le texte reste modifiable avant l'envoi). */
export const REFUSAL_REASON_PRESETS = [
  'La tenue n\'est pas dans l\'état annoncé.',
  'La pièce n\'a pas été rapportée à la logistique.',
  'La taille demandée n\'est pas disponible : garde ta tenue actuelle.',
  'Usure normale : pas de remplacement prévu pour ce type de tenue.',
] as const

export const MAX_RETURN_COMMENT = 2000
export const MAX_RETURN_ITEMS = 50

export interface ReturnRequestInput {
  reason: string
  comment?: string | null
}

/** Retourne un message d'erreur (FR) ou null. */
export const validateReturnRequest = (input: ReturnRequestInput): string | null => {
  if (!(input.reason in GARMENT_RETURN_REASONS)) return 'Le motif du retour est obligatoire.'
  const comment = (input.comment ?? '').trim()
  if (input.reason === 'AUTRE' && comment === '') return 'Précise le motif du retour dans le commentaire.'
  if (comment.length > MAX_RETURN_COMMENT) return 'Le commentaire est trop long.'
  return null
}

export interface DecisionInput {
  decision: string
  reason?: string | null
  outcome?: string | null
  refunded_points?: number | null
  restock?: boolean | null
  replacement_holding_id?: string | null
}

/** Retourne un message d'erreur (FR) ou null. */
export const validateDecision = (input: DecisionInput): string | null => {
  const reason = (input.reason ?? '').trim()
  if (input.decision === 'REFUSED') {
    if (reason === '') return 'Un motif de refus est obligatoire.'
    return null
  }
  if (input.decision !== 'ACCEPTED') return 'Décision invalide.'
  if (!input.outcome || !(input.outcome in GARMENT_RETURN_OUTCOMES)) return 'L\'issue du retour est obligatoire.'
  if (typeof input.restock !== 'boolean') return 'Indique si la tenue retourne en stock ou au rebut.'
  if (input.outcome === 'REFUND_POINTS') {
    const points = Number(input.refunded_points)
    if (!Number.isFinite(points) || points <= 0) return 'Le montant remboursé doit être supérieur à zéro.'
  }
  if (input.outcome !== 'REFUND_POINTS' && input.refunded_points) return 'Un montant n\'est possible que pour un remboursement.'
  if (input.outcome !== 'EXCHANGE' && input.replacement_holding_id) return 'Une tenue de remplacement n\'est possible que pour un échange.'
  return null
}

export const garmentReturnStatusColor = (status: string): 'info' | 'success' | 'error' | 'neutral' => {
  if (status === 'PENDING') return 'info'
  if (status === 'ACCEPTED') return 'success'
  if (status === 'REFUSED') return 'error'
  return 'neutral'
}

// ── Catalogue des types (source unique : provisionnement + import) ───────────

export interface GarmentTypeSeed {
  code: string
  legacy_label: string
  label: string
  family: string
  service: string | null
  gender: 'M' | 'F' | 'U'
  size_system: SizeSystem
}

const t = (code: string, legacy_label: string, family: string, service: string | null, size_system: SizeSystem, gender: 'M' | 'F' | 'U' = 'U'): GarmentTypeSeed =>
  ({ code, legacy_label, label: legacy_label, family, service, gender, size_system })

export const GARMENT_TYPE_SEEDS: GarmentTypeSeed[] = [
  t('TSHIRT_AP', 'T-Shirt AP', 'T-shirt', 'AP', 'alpha'),
  t('TSHIRT_AP_ETE', 'T-Shirt Eté AP', 'T-shirt', 'AP', 'alpha'),
  t('TSHIRT_AP_LOG', 'T-Shirt AP Log', 'T-shirt', 'LOG', 'alpha'),
  t('TSHIRT_AP_FOR_H', 'T-Shirt AP For H', 'T-shirt', 'FOR', 'alpha', 'M'),
  t('TSHIRT_AP_FOR_F', 'T-Shirt AP For F', 'T-shirt', 'FOR', 'alpha', 'F'),
  t('POLO_AP', 'Polo AP', 'Polo', 'AP', 'alpha'),
  t('POLO_AMU', 'Polo AMU', 'Polo', 'AMU', 'alpha'),
  t('PULL_AP', 'Pull AP', 'Pull', 'AP', 'alpha'),
  t('PULL_AP_LOG', 'Pull AP Log', 'Pull', 'LOG', 'alpha'),
  t('PULL_AMU', 'Pull AMU', 'Pull', 'AMU', 'alpha'),
  t('SWEAT_CAPUCHE_AP', 'Sweat Capuche AP', 'Sweat', 'AP', 'alpha'),
  t('BLOUSE_INFIRMIER_AP', 'Blouse Infirmier AP', 'Blouse', 'AP', 'alpha'),
  t('SOFTSHELL_AMU', 'Softshell AMU', 'Softshell', 'AMU', 'alpha'),
  t('SOFTSHELL_AP', 'Softshell AP', 'Softshell', 'AP', 'alpha'),
  t('PARKA_AMU', 'Parka AMU', 'Parka', 'AMU', 'alpha'),
  t('BLOUSON_AMU', 'Blouson AMU', 'Blouson', 'AMU', 'alpha'),
  t('GILET_AMU', 'Gilet AMU', 'Gilet', 'AMU', 'alpha'),
  t('VESTE_FLUO_P', 'Veste Fluo P', 'Veste', null, 'alpha'),
  t('PANTALON_AMU', 'Pantalon AMU', 'Pantalon', 'AMU', 'numeric'),
  t('PANTALON_AMU_FEMMES', 'Pantalon AMU Femmes', 'Pantalon', 'AMU', 'numeric', 'F'),
  t('PANTALON_AMU_LETTRE', 'Pantalon AMU (Lettre)', 'Pantalon', 'AMU', 'alpha'),
  t('PANTALON_LOG', 'Pantalon LOG', 'Pantalon', 'LOG', 'numeric'),
  t('PATCH_AP', 'Patch AP', 'Accessoire', 'AP', 'none'),
  t('PLAQUE_PARE_BRISE', 'Plaque Pare Brise', 'Accessoire', null, 'none'),
  t('PLAQUE_PARE_SOLEIL', 'Plaque Pare Soleil', 'Accessoire', null, 'none'),
  t('GENOUILLERES', 'Genouillères', 'Protection', null, 'none'),
  t('BONNET_AP', 'Bonnet AP', 'Accessoire', 'AP', 'none'),
  t('CACHE_OREILLE', 'Cache oreille', 'Accessoire', null, 'none'),
  t('TOUR_DE_COU', 'Tour de Cou', 'Accessoire', null, 'none'),
  t('OREILLETTE_T320', 'Oreillette T320', 'Télécom', 'T320', 'none'),
  t('MICRO_DEPORTE_T320', 'Micro Déporté T320', 'Télécom', 'T320', 'none'),
]

// ── Regroupement d'affichage ─────────────────────────────────────────────────

export interface GroupableHolding {
  id: string
  status: string
  size?: string | null
  brand_model?: string | null
  serial_number?: string | null
  inventory_number?: string | null
  expires_at?: string | null
  rescue_center?: number | null
  storage_location?: string | null
  needs_review?: boolean | null
  review_reason?: string | null
  legacy_holder_label?: string | null
  assigned_at?: string | null
  garment_type?: { id?: string } | string | null
  user?: { id?: string } | string | null
}

const refId = (value: { id?: string } | string | null | undefined) => (value && typeof value === 'object' ? value.id ?? '' : value ?? '')

/**
 * Clé de regroupement : des pièces identiques détenues par le même volontaire (ou en stock au même endroit) s'affichent sur une seule ligne.
 * Une pièce suivie à l'unité (n° de série, n° d'inventaire, date de péremption) n'est jamais regroupée.
 */
export const holdingGroupKey = (h: GroupableHolding): string => {
  if (h.serial_number || h.inventory_number || h.expires_at) return `single:${h.id}`
  return [
    h.status, refId(h.garment_type), h.size ?? '', h.brand_model ?? '',
    refId(h.user) || `label:${h.legacy_holder_label ?? ''}`,
    h.rescue_center ?? '', h.storage_location ?? '', h.needs_review ? '1' : '0', h.review_reason ?? '',
  ].join('|')
}

/** Regroupe en conservant l'ordre : la première pièce représente le groupe (`assigned_at` = la plus ancienne). */
export const groupHoldings = <T extends GroupableHolding>(rows: T[]): Array<T & { count: number, ids: string[] }> => {
  const groups = new Map<string, T & { count: number, ids: string[] }>()
  for (const row of rows) {
    const key = holdingGroupKey(row)
    const found = groups.get(key)
    if (!found) {
      groups.set(key, { ...row, count: 1, ids: [row.id] })
      continue
    }
    found.count++
    found.ids.push(row.id)
    if (row.assigned_at && (!found.assigned_at || row.assigned_at < found.assigned_at)) found.assigned_at = row.assigned_at
  }
  return [...groups.values()]
}

// ── Seuils de stock, péremption, inventaire ──────────────────────────────────

export type StockLevel = 'ok' | 'low' | 'out'

/** `min` vide ou 0 = pas de seuil. Rupture = stock nul, bas = stock inférieur au seuil. */
export const stockLevel = (count: number, min: number | null | undefined): StockLevel => {
  const threshold = Number(min ?? 0)
  if (!(threshold > 0)) return 'ok'
  if (count <= 0) return 'out'
  return count < threshold ? 'low' : 'ok'
}

export interface StockRow { garment_type: string, size: string | null, rescue_center: number | null, count: number }
/** Seuil : `size` et `rescue_center` vides = toutes tailles / tous centres confondus. */
export interface StockThreshold { id?: string, garment_type: string, size: string | null, rescue_center: number | null, min_quantity: number }

export interface ThresholdAlert { threshold: StockThreshold, current: number, level: Exclude<StockLevel, 'ok'> }

/** Compare chaque seuil au stock correspondant ; ne renvoie que les seuils en rupture ou bas (ruptures d'abord). */
export const evaluateThresholds = (thresholds: StockThreshold[], stock: StockRow[]): ThresholdAlert[] => {
  const alerts: ThresholdAlert[] = []
  for (const threshold of thresholds) {
    const current = stock
      .filter(r => r.garment_type === threshold.garment_type
        && (threshold.size === null || r.size === threshold.size)
        && (threshold.rescue_center === null || r.rescue_center === threshold.rescue_center))
      .reduce((n, r) => n + r.count, 0)
    const level = stockLevel(current, threshold.min_quantity)
    if (level !== 'ok') alerts.push({ threshold, current, level })
  }
  return alerts.sort((a, b) => (a.level === b.level ? 0 : a.level === 'out' ? -1 : 1))
}

export const EXPIRY_WARNING_DAYS = 90

/** `expired` = date dépassée, `soon` = expire dans les `days` jours. Dates au format AAAA-MM-JJ. */
export const expiryState = (expiresAt: string | null | undefined, now: Date, days = EXPIRY_WARNING_DAYS): 'expired' | 'soon' | null => {
  if (!expiresAt || !/^\d{4}-\d{2}-\d{2}/.test(expiresAt)) return null
  const today = now.toISOString().slice(0, 10)
  const limit = new Date(now.getTime() + days * 86_400_000).toISOString().slice(0, 10)
  const date = expiresAt.slice(0, 10)
  if (date < today) return 'expired'
  return date <= limit ? 'soon' : null
}

export interface InventoryCount { garment_type: string, size: string | null, count: number }
export interface InventoryGap extends InventoryCount { expected: number, counted: number, delta: number }

const countKey = (c: { garment_type: string, size: string | null }) => `${c.garment_type}|${c.size ?? ''}`

/** Écarts entre le stock du registre et le comptage physique (delta < 0 : pièces manquantes, > 0 : pièces en trop). */
export const diffInventoryCounts = (expected: InventoryCount[], counted: InventoryCount[]): InventoryGap[] => {
  const map = new Map<string, InventoryGap>()
  const entry = (c: InventoryCount) => {
    const key = countKey(c)
    if (!map.has(key)) map.set(key, { garment_type: c.garment_type, size: c.size ?? null, count: 0, expected: 0, counted: 0, delta: 0 })
    return map.get(key)!
  }
  for (const c of expected) entry(c).expected += c.count
  for (const c of counted) entry(c).counted += c.count
  return [...map.values()]
    .map(g => ({ ...g, count: Math.abs(g.counted - g.expected), delta: g.counted - g.expected }))
    .filter(g => g.delta !== 0)
}

/** Numéro de série / d'inventaire comparable (casse, espaces). */
export const normalizeIdentifier = (raw: unknown): string => String(raw ?? '').trim().toUpperCase().replace(/\s+/g, '')

/** Écarts par identifiant : `missing` = au registre mais non scannées, `unknown` = scannées mais absentes du stock du centre. */
export const diffIdentifiers = (expected: Array<{ id: string, identifiers: string[] }>, scanned: string[]) => {
  const scannedSet = new Set(scanned.map(normalizeIdentifier).filter(Boolean))
  const known = new Set<string>()
  const missing: string[] = []
  for (const piece of expected) {
    const ids = piece.identifiers.map(normalizeIdentifier).filter(Boolean)
    ids.forEach(i => known.add(i))
    if (ids.length && !ids.some(i => scannedSet.has(i))) missing.push(piece.id)
  }
  return { missing, unknown: [...scannedSet].filter(i => !known.has(i)) }
}

// ── Statistiques des retours ─────────────────────────────────────────────────

export interface ReturnStatRow {
  status: string
  date_created: string
  decided_at?: string | null
  refunded_points?: number | null
  refunded_at?: string | null
  type_label?: string | null
}

const dayMs = 86_400_000

/** Synthèse des demandes de retour : volumes, délai moyen de décision, points remboursés par mois et retours par type. */
export const summarizeReturns = (rows: ReturnStatRow[]) => {
  const byStatus: Record<string, number> = {}
  const byType = new Map<string, number>()
  const refunded = new Map<string, number>()
  let delaySum = 0
  let delayCount = 0
  for (const row of rows) {
    byStatus[row.status] = (byStatus[row.status] ?? 0) + 1
    const label = row.type_label || 'Inconnu'
    byType.set(label, (byType.get(label) ?? 0) + 1)
    if (row.decided_at && (row.status === 'ACCEPTED' || row.status === 'REFUSED')) {
      const delay = Date.parse(row.decided_at) - Date.parse(row.date_created)
      if (Number.isFinite(delay) && delay >= 0) {
        delaySum += delay
        delayCount++
      }
    }
    const points = Number(row.refunded_points ?? 0)
    if (points > 0 && row.refunded_at) refunded.set(row.refunded_at.slice(0, 7), (refunded.get(row.refunded_at.slice(0, 7)) ?? 0) + points)
  }
  return {
    total: rows.length,
    byStatus,
    avgDecisionDays: delayCount ? Math.round((delaySum / delayCount / dayMs) * 10) / 10 : null,
    refundedByMonth: [...refunded.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([month, points]) => ({ month, points })),
    byType: [...byType.entries()].sort(([, a], [, b]) => b - a).map(([label, count]) => ({ label, count })),
  }
}
